'use client';

import {
  useCurrentUser,
  useUserResourceSource,
  type Enrollment,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { CardSkeleton, IslandKeyframes } from './_shared';

/**
 * Inline progress bar showing the signed-in user's progress for a
 * specific course. Renders nothing when anonymous or no enrollment
 * exists.
 */
export interface ProgressBarProps {
  courseSlug: string;
  showLabel?: boolean;
  height?: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function ProgressBar({ courseSlug, showLabel = true, height = 6 }: ProgressBarProps) {
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const state = useAsyncResource<Enrollment>(
    async (signal) => {
      if (!user || !ur?.courseProgress) return null;
      return await ur.courseProgress.fetch(courseSlug, signal);
    },
    [user, ur, courseSlug],
  );

  if (!user || !ur?.courseProgress) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <CardSkeleton height={height + (showLabel ? 24 : 0)} />
      </>
    );
  if (state.kind !== 'loaded') return null;

  const pct = clamp(state.data.progressPercent ?? 0, 0, 100);
  return (
    <div>
      <div
        style={{
          height,
          background: 'var(--color-neutral-100, #f4f4f5)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--color-brand-500, #3b5bdb)',
            transition: 'width 400ms ease',
          }}
        />
      </div>
      {showLabel && (
        <div
          style={{
            marginTop: 6,
            fontSize: '11px',
            color: 'var(--color-text-secondary, #71717a)',
            fontFamily: 'var(--font-family-mono, monospace)',
          }}
        >
          {state.data.completedAt ? '✓ Completed' : `${Math.round(pct)}% complete`}
        </div>
      )}
    </div>
  );
}
